// Fires the Upgate checkout for the paywall popup. Authorizes via the
// x-auth-token header (not a cookie) so it works cross-origin, where a prelander
// can't set a cookie for the API's domain. baseUrl returns the user to /lobby.
import { useState } from 'react';
import { applyPriceMultiplier, resolvePriceMultiplier } from '../../utils/paywall';
import { markCheckoutStarted } from '../../utils/session';

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

const PLATFORM_BASE_URL =
  process.env.REACT_APP_PLATFORM_BASE_URL || SWIPEY_BASE_URL;

const MODAL_SOURCE = 'paywall_popup';

/**
 * Build the post-payment return URL the BE turns into Upgate's success_url.
 * `paywallType` ('paywall_popup') rides as a query param so the platform /lobby
 * can tag subscription_purchase with the experiment variant — uniformly for
 * google + password + magic-link popup buyers (google's OAuth `type` is the model
 * style, so it can't carry the popup marker any other way). Requires 'paywall_type'
 * in the backend ALLOWED_REDIRECT_PARAMS allowlist, else it's stripped before the
 * success_url is built.
 *
 * create-friend-white is the SFW lander variant — protection=sfw rides along so the
 * post-payment /lobby renders the SFW experience, matching every other white handoff.
 */
// The authToken behind a checkout is gone (spent by the platform, or past the
// backend's 1h Redis TTL) — the caller must re-authenticate rather than retry.
export const isAuthCheckoutError = (err) =>
  err?.status === 401 || err?.status === 403;

export const buildLobbyBaseUrl = (authToken, paywallType) =>
  `${PLATFORM_BASE_URL}/lobby?auth_token=${encodeURIComponent(authToken || '')}&protection=sfw${
    paywallType ? `&paywall_type=${encodeURIComponent(paywallType)}` : ''
  }`;

// Checkout texts shown on the Upgate hosted page, mirroring the platform's
// CheckoutModal subscription branch (frontend CheckoutModal/context.tsx):
// productName → title, description → subtitle, renewalDescription → the
// fine print the backend injects via payment_form_override CSS.
// The popup is English-only, so the strings are built in English here.
const buildCheckoutTexts = (
  plan,
  { bonusTokens, currencySymbol, isFlatRebill, priceMultiplier },
) => {
  const durationLabel = `${plan.duration_months} ${
    plan.duration_months > 1 ? 'months' : 'month'
  } subscription`;

  const productName = plan.discount_percent
    ? `${durationLabel} (${plan.discount_percent}% Off)`
    : durationLabel;

  const description = bonusTokens > 0 ? `Bonus Hearts: +${bonusTokens}` : '';

  // Renews at the full (undiscounted) price unless the visitor is flat-rebill,
  // which keeps charging the same amount — same rule as the platform.
  // `plan.isFlatRebill` is the platform's signal, but the popup fetches plans
  // unauthenticated (x-api-key only), so the backend has no user to resolve the
  // experiment against and always returns false — hence the caller-supplied variant
  // from GrowthBook (isFlatRebill in GrowthBookContext). Either source saying flat wins.
  const flatRebill = plan.isFlatRebill || isFlatRebill;

  // Scaled up by the geo price multiplier: the plans this popup fetched are
  // unmultiplied (GET /subscription-plans only applies it for an authenticated user),
  // while the backend bills the multiplied amount — so every figure printed here has
  // to be scaled to match what is actually charged.
  //
  // The multiplier goes on the same PER-FIELD amounts the backend multiplies
  // (subscription-plans.service.applyLocationBasedPriceMultiplier → monthly_price /
  // paymentAmount), never on a total: applyMultiplier ends every amount in .99, so
  // rounding the total instead would quote $119.99 where the rebill actually charges
  // 12 × $9.99 = $119.88 (payments.service.createRebillPaymentWithTokenForSubscription).
  const multiplier = resolvePriceMultiplier(priceMultiplier);
  const paymentAmount = applyPriceMultiplier(plan.paymentAmount, multiplier);
  const renewAmount = flatRebill
    ? paymentAmount
    : plan.duration_months * applyPriceMultiplier(plan.monthly_price, multiplier);

  let renewalDescription = '';
  if (Number.isFinite(renewAmount) && renewAmount > 0) {
    // Multiplied too — it is the amount Upgate is about to charge on this page, so
    // printing the base price here would contradict the hosted page's own total.
    const todayPart = flatRebill
      ? ''
      : `${currencySymbol}${paymentAmount.toFixed(2)} today • `;
    const afterLabel = `${plan.duration_months} ${
      plan.duration_months > 1 ? 'Months' : 'Month'
    }`;
    renewalDescription = `${todayPart}Premium Renews at ${currencySymbol}${renewAmount.toFixed(2)} after ${afterLabel}. Cancel anytime.`;
  }

  return { productName, description, renewalDescription };
};

// Plain (hook-free) checkout call → { redirectUrl, ... }; navigates on success.
// Exported on its own because the popup_first order has no popup mounted at
// checkout time: createAccount fires the checkout straight after registration with
// the freshly-minted authToken, which a hook instantiated on mount wouldn't have.
//
// bonusTokens: the heart-pill value for the plan (keeps the checkout text in sync
// with what the popup displayed); currencySymbol: geo symbol from
// useCurrentCurrency; locale: lander i18n language for the checkout page;
// isFlatRebill: the visitor's exp_full_rebill variant, which decides the renewal
// price in the fine print; priceMultiplier: their location-based-price-multiplier
// arm, which scales both printed amounts (see buildCheckoutTexts).
export const runUpgateCheckout = async (
  plan,
  {
    authToken,
    redirect = true,
    bonusTokens = 0,
    currencySymbol = '$',
    locale,
    paywallType,
    isFlatRebill = false,
    priceMultiplier,
  } = {},
) => {
  if (!plan?._id) throw new Error('checkout: plan._id is required');

  const { productName, description, renewalDescription } = buildCheckoutTexts(
    plan,
    { bonusTokens, currencySymbol, isFlatRebill, priceMultiplier },
  );

  const body = {
    baseUrl: buildLobbyBaseUrl(authToken, paywallType),
    productName,
    description,
    renewalDescription,
    ...(locale && { locale }),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    modal_source: MODAL_SOURCE,
    usePaywallInitialTokensBonus: true,
  };

  const res = await fetch(
    `${API_BASE_URL}/subscription-plans/${plan._id}/upgate-checkout-external`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        'x-auth-token': authToken,
      },
      body: JSON.stringify(body),
    },
  );
  const data = await res.json();
  if (!res.ok) {
    // Carry the status so callers can tell "this token is dead" (401/403 — spent
    // by the platform or aged out of Redis) from a transient failure the user can
    // simply retry. See isAuthCheckoutError.
    const error = new Error(data?.message || `HTTP error! status: ${res.status}`);
    error.status = res.status;
    throw error;
  }

  if (redirect && data?.redirectUrl) {
    // Leaving for the hosted checkout. Stamp the stored session so a return —
    // Back from Upgate, a cancel bounce, a bfcache restore — reopens the paywall
    // popup with this token instead of the signup form (see utils/session).
    markCheckoutStarted();
    window.location.href = data.redirectUrl;
  }
  return data;
};

// authToken: registration key, sent as x-auth-token + embedded in baseUrl.
export function useUpgateCheckout({ authToken } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkout = async (plan, opts = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      return await runUpgateCheckout(plan, { ...opts, authToken });
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { checkout, isLoading, error };
}
